/**
 * Central status dictionaries for orders, medication requests, and investigations.
 *
 * Each entry maps a human-readable label to the backend value (wc-* slug).
 * The order of entries reflects the typical workflow progression.
 *
 * Source of truth: e-pharmacy-api
 *   - Order statuses:      library/order/status.php + WooCommerce defaults
 *   - Order item statuses: library/order/item/status.php
 */

// ── Order statuses ───────────────────────────────────────────────────

export interface StatusEntry {
  label: string
  value: string
}

/**
 * All non-deprecated order statuses, ordered by typical workflow progression.
 *
 * Values use the `wc-` prefix expected by the PHP API's
 * `PATCH /order/{hash}/status` endpoint.
 */
export const ORDER_STATUSES: readonly StatusEntry[] = [
  { label: 'Pending Payment', value: 'wc-pending' },
  { label: 'Processing', value: 'wc-processing' },
  { label: 'Public Processing', value: 'wc-public-processing' },
  { label: 'On Hold', value: 'wc-on-hold' },
  { label: 'Clinical Bypass', value: 'wc-clinical-bypass' },
  { label: 'Awaiting Decision', value: 'wc-awaiting-decision' },
  { label: 'Awaiting Vendor Decision', value: 'wc-vendor-decision' },
  { label: 'Ready for Fulfillment', value: 'wc-ready-for-fulfillment' },
  { label: 'Pharmacist Check', value: 'wc-pharmacist-check' },
  { label: 'Stock Issues', value: 'wc-stock-issues' },
  { label: 'Picked', value: 'wc-picked' },
  { label: 'Returned for Additional Check', value: 'wc-pharmacist-returned-for-additional-check' },
  { label: 'Dispensed', value: 'wc-dispensed' },
  { label: 'Packed', value: 'wc-packed' },
  { label: 'With Pharmacy', value: 'wc-with-pharmacy' },
  { label: 'Awaiting Dispatch', value: 'wc-awaiting-dispatch' },
  { label: 'Dispatched', value: 'wc-dispatched' },
  { label: 'Delivered', value: 'wc-delivered' },
  { label: 'Investigation Waiting', value: 'wc-inv-waiting' },
  { label: 'Completed', value: 'wc-completed' },
  { label: 'Returned', value: 'wc-returned' },
  { label: 'Cancelled', value: 'wc-cancelled' },
  { label: 'Refunded', value: 'wc-refunded' },
  // Subscription-specific statuses (shop_subscription records)
  { label: 'Active', value: 'wc-active' },
  { label: 'Pending Cancellation', value: 'wc-pending-cancel' },
  { label: 'Expired', value: 'wc-expired' },
  { label: 'Switched', value: 'wc-switched' }
] as const

/** Label strings only — for use in dropdown menus. */
export const ORDER_STATUS_LABELS = ORDER_STATUSES.map(s => s.label)

// ── Shared order-item statuses (med-reqs & investigations) ──────────

const SHARED_ORDER_ITEM_STATUSES: readonly StatusEntry[] = [
  { label: 'Created', value: 'wc-item-created' },
  { label: 'Duplicate Check', value: 'wc-item-duplicate-check' },
  { label: 'System ID Check', value: 'wc-item-system-id-check' },
  { label: 'Approved', value: 'wc-item-approved' },
  { label: 'Ready for Fulfillment', value: 'wc-item-ready-for-fulfillment' },
  { label: 'Pharmacist Check', value: 'wc-item-pharmacist-check' },
  { label: 'Picked', value: 'wc-item-picked' },
  { label: 'Dispensed', value: 'wc-item-dispensed' },
  { label: 'Packed', value: 'wc-item-packed' },
  { label: 'Rejected', value: 'wc-item-rejected' },
  { label: 'Refunded', value: 'wc-item-refunded' }
] as const

// ── Medication request (order-item) statuses ─────────────────────────

/**
 * Medication-request order-item statuses (clinical workflow) — every status the PHP API accepts
 * except the investigation ones, in workflow order so the clinical stages sit near the top of the
 * dropdown. Values use the `wc-item-` prefix the API expects.
 */
export const MED_REQUEST_STATUSES: readonly StatusEntry[] = [
  // Intake and automated gates
  { label: 'Created', value: 'wc-item-created' },
  { label: 'Duplicate Check', value: 'wc-item-duplicate-check' },
  { label: 'System ID Check', value: 'wc-item-system-id-check' },
  { label: 'System SCR Check', value: 'wc-item-system-scr-check' },
  // Not an accepted PHP status; kept only to match NodeAPI's display map.
  { label: 'Decision Assistant Check', value: 'wc-item-decision-assistant-check' },
  { label: 'System FAN Check', value: 'wc-item-system-fan-check' },
  { label: 'Awaiting Vendor Decision', value: 'wc-item-awaiting-vendor-decision' },

  // Clinical decision
  { label: 'Clinical Review', value: 'wc-item-clinical-review' },
  { label: 'Clinical Query', value: 'wc-item-clinical-query' },
  { label: 'Clinical FAN', value: 'wc-item-clinical-fan' },
  // Alternate label for the same value; `dedupeByValue` collapses the pair and keeps this one.
  { label: 'Clinical Further Answers Needed', value: 'wc-item-clinical-fan' },
  { label: 'Clinical Video Consultation', value: 'wc-item-clinical-video' },

  // Decision outcome
  { label: 'Approved', value: 'wc-item-approved' },
  { label: 'Approved for Future', value: 'wc-item-approved-future' },
  { label: 'On Hold', value: 'wc-item-on-hold' },
  { label: 'Rejected', value: 'wc-item-rejected' },

  // Fulfillment
  { label: 'Ready for Fulfillment', value: 'wc-item-ready-for-fulfillment' },
  { label: 'Pharmacist Check', value: 'wc-item-pharmacist-check' },
  { label: 'Picked', value: 'wc-item-picked' },
  { label: 'Dispensed', value: 'wc-item-dispensed' },
  { label: 'Packed', value: 'wc-item-packed' },
  { label: 'Refunded', value: 'wc-item-refunded' }
] as const

const ID_VERIFICATION_RESET_BLOCKED_STATUSES: ReadonlyMap<string, string> = new Map([
  ['wc-item-ready-for-fulfillment', 'is already with the pharmacy'],
  ['wc-item-pharmacist-check', 'is already with the pharmacy'],
  ['wc-item-picked', 'has already been picked'],
  ['wc-item-dispensed', 'has already been dispensed'],
  ['wc-item-packed', 'has already been packed'],
  ['wc-item-rejected', 'has been rejected'],
  ['wc-item-refunded', 'has been refunded']
])

export function getIdVerificationResetBlocker(stage: string | null | undefined): string | null {
  if (!stage) return 'has no stage recorded'

  const value = medReqLabelLowerToValue.get(stage.toLowerCase()) ?? stage
  const blocked = ID_VERIFICATION_RESET_BLOCKED_STATUSES.get(value)
  if (blocked) return blocked

  return medReqValueToLabel.has(value) ? null : 'is at an unrecognised stage'
}

/** Label strings only — for use in dropdown menus. */
export const MED_REQUEST_STATUS_LABELS = MED_REQUEST_STATUSES.map(s => s.label)

/**
 * Statuses only reachable once a prescriber has decided the item. On Hold belongs here — it means
 * approved-awaiting-payment; the clinical holds are Query/FAN/Video. Rejected and Refunded do not,
 * as both are reachable without an approval.
 */
export const POST_APPROVAL_STATUSES: readonly string[] = [
  'wc-item-approved',
  'wc-item-approved-future',
  'wc-item-on-hold',
  'wc-item-ready-for-fulfillment',
  'wc-item-pharmacist-check',
  'wc-item-picked',
  'wc-item-dispensed',
  'wc-item-packed'
] as const

// ── Investigation (order-item) statuses ──────────────────────────────

/**
 * Investigation order-item statuses (investigation workflow).
 * Shared statuses + investigation-specific statuses.
 *
 * Values use the `wc-item-` prefix expected by the PHP API's
 * `PATCH /order/item/{hash}/status` endpoint.
 */
export const INVESTIGATION_STATUSES: readonly StatusEntry[] = [
  ...SHARED_ORDER_ITEM_STATUSES,
  { label: 'Investigation Started', value: 'wc-item-investigation-start' },
  { label: 'Investigation Pending', value: 'wc-item-investigation-pending' },
  { label: 'Investigation Results Ready', value: 'wc-item-investigation-results-ready' },
  { label: 'Investigation Results Shared', value: 'wc-item-investigation-results-shared' }
] as const

/** Label strings only — for use in dropdown menus. */
export const INVESTIGATION_STATUS_LABELS = INVESTIGATION_STATUSES.map(s => s.label)

// ── Lookup helpers ───────────────────────────────────────────────────

const orderLabelToValue = new Map(ORDER_STATUSES.map(s => [s.label, s.value]))
const orderValueToLabel = new Map(ORDER_STATUSES.map(s => [s.value, s.label]))
const medReqLabelToValue = new Map(MED_REQUEST_STATUSES.map(s => [s.label, s.value]))
const medReqValueToLabel = new Map(MED_REQUEST_STATUSES.map(s => [s.value, s.label]))
/** Case-insensitive label index: the API capitalises differently ("Ready For Fulfillment"). */
const medReqLabelLowerToValue = new Map(MED_REQUEST_STATUSES.map(s => [s.label.toLowerCase(), s.value]))
const investigationLabelToValue = new Map(INVESTIGATION_STATUSES.map(s => [s.label, s.value]))
const investigationValueToLabel = new Map(INVESTIGATION_STATUSES.map(s => [s.value, s.label]))

/** Combined value→label map across all status types (orders, med-reqs, investigations). */
const allValueToLabel = new Map([...orderValueToLabel, ...medReqValueToLabel, ...investigationValueToLabel])

/**
 * Convert an order status label to its `wc-*` backend value.
 * Returns `undefined` if the label is not found.
 */
export function orderStatusLabelToValue(label: string): string | undefined {
  return orderLabelToValue.get(label)
}

/**
 * Convert a `wc-*` backend value to a human-readable order status label.
 * Handles both prefixed (`wc-processing`) and stripped (`processing`) values.
 * Falls back to the raw value if not found.
 */
export function orderStatusValueToLabel(value: string): string {
  const normalized = value.startsWith('wc-') ? value : `wc-${value}`
  return orderValueToLabel.get(normalized) ?? value
}

/**
 * Convert a med-request status label to its `wc-item-*` backend value.
 * Returns `undefined` if the label is not found.
 */
export function medRequestStatusLabelToValue(label: string): string | undefined {
  return medReqLabelToValue.get(label)
}

/**
 * Convert a `wc-item-*` backend value to a human-readable med-request status label.
 * Falls back to the raw value if not found.
 */
export function medRequestStatusValueToLabel(value: string): string {
  return medReqValueToLabel.get(value) ?? value
}

/** Whether a med-request status label is post-approval — labels are what the timeline stages speak. */
export function isPostApprovalStatusLabel(label: string): boolean {
  const value = medRequestStatusLabelToValue(label)
  return value !== undefined && POST_APPROVAL_STATUSES.includes(value)
}

/**
 * Convert an investigation status label to its `wc-item-*` backend value.
 * Returns `undefined` if the label is not found.
 */
export function investigationStatusLabelToValue(label: string): string | undefined {
  return investigationLabelToValue.get(label)
}

/**
 * Convert a `wc-item-*` backend value to a human-readable investigation status label.
 * Falls back to the raw value if not found.
 */
export function investigationStatusValueToLabel(value: string): string {
  return investigationValueToLabel.get(value) ?? value
}

/**
 * Replace all `wc-*` / `wc-item-*` backend status slugs in a message
 * with their human-readable UI labels.
 *
 * E.g. "Invalid status transition from wc-item-created, to wc-item-rejected"
 *    → "Invalid status transition from Created, to Rejected"
 */
export function humanizeStatusMessage(message: string): string {
  return message.replace(/wc-item-[\w-]+|wc-[\w-]+/g, match => allValueToLabel.get(match) ?? match)
}
